import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useSupabase } from '../../hooks/useSupabase';
import { useAuth } from '../../contexts/AuthContext';

// Import all components
import NumericKeypad from './components/NumericKeypad';
import EmployeeCodeInput from './components/EmployeeCodeInput';
import ClockActionButtons from './components/ClockActionButtons';
import QRCodeScanner from './components/QRCodeScanner';
import AccessibilityControls from './components/AccessibilityControls';
import IdleTimeoutHandler from './components/IdleTimeoutHandler';
import KioskHeader from './components/KioskHeader';
import KioskFooter from './components/KioskFooter';

// Save local German time without timezone conversion
const getCurrentLocalTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');

  // Return timestamp without timezone info (postgres will treat as local)
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;
};

// Get adjusted clock-in time: add 15 minutes, then round to nearest 15-minute interval
const getAdjustedClockInTime = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeString = now.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  // Step 1: Add 15 minutes to current time
  let adjustedHour = currentHour;
  let adjustedMinute = currentMinute + 15;

  // Handle minute overflow (e.g., 10:45 + 15 = 11:00)
  if (adjustedMinute >= 60) {
    adjustedHour += 1;
    adjustedMinute -= 60;
  }

  // Step 2: Round to nearest 15-minute interval
  const roundedMinute = Math.round(adjustedMinute / 15) * 15;
  let finalHour = adjustedHour;
  let finalMinute = roundedMinute;

  // Handle minute overflow again after rounding
  if (finalMinute >= 60) {
    finalHour += 1;
    finalMinute = 0;
  }

  // Apply 8:00 AM minimum start time
  if (finalHour < 8 || (finalHour === 8 && finalMinute === 0)) {
    console.log(`Early clock-in detected: ${currentTimeString} -> Adjusting to 8:00 AM`);
    return `${year}-${month}-${day} 08:00:00.000`;
  }

  // Apply the rounded time
  const roundedTimeString = `${String(finalHour).padStart(2, '0')}:${String(finalMinute).padStart(2, '0')}:00`;
  console.log(`Clock-in time: ${currentTimeString} -> +15min -> ${adjustedHour}:${String(adjustedMinute).padStart(2, '0')} -> rounded to ${roundedTimeString}`);
  
  return `${year}-${month}-${day} ${roundedTimeString}.000`;
};

// Get clock-out time (no adjustments, use actual time)
const getAdjustedClockOutTime = () => {
  const now = new Date();
  const currentTimeString = now.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  console.log(`Clock-out time: ${currentTimeString} (no adjustments)`);
  
  // Return actual current time without any modifications
  return getCurrentLocalTime();
};

const KioskClockInterface = () => {
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const { signOut } = useAuth();
  
  // State management
  const [employeeCode, setEmployeeCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [recentPunches, setRecentPunches] = useState(new Set());
  const [error, setError] = useState(null);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [isCompact, setIsCompact] = useState(false);
  const [showAlreadyClockedInModal, setShowAlreadyClockedInModal] = useState(false);

  // Ensure the kiosk always matches the visible viewport height (iOS Safari friendly)
  useEffect(() => {
    const setAppVh = () => {
      const vhUnit = window?.innerHeight ? window.innerHeight * 0.01 : 0;
      if (vhUnit) {
        document.documentElement?.style?.setProperty('--app-vh', `${vhUnit}px`);
      }
      // Toggle compact layout for short viewports
      setIsCompact(window?.innerHeight && window.innerHeight < 740);
    };

    setAppVh();
    window.addEventListener('resize', setAppVh);
    window.addEventListener('orientationchange', setAppVh);
    return () => {
      window.removeEventListener('resize', setAppVh);
      window.removeEventListener('orientationchange', setAppVh);
    };
  }, []);

  // Intercept browser back to logout and redirect to login
  useEffect(() => {
    // Push a history entry to trap back navigation on kiosk
    try {
      window?.history?.pushState({ kiosk: true }, '', window?.location?.href);
    } catch {}

    const handlePopState = async () => {
      try {
        await signOut();
      } catch (e) {
        console.error('Error during sign out on back:', e);
      } finally {
        navigate('/login', { replace: true });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, signOut]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Look up employee by employee code
  const lookupEmployee = async (code) => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          user_profile:user_profiles(*)
        `)
        .eq('employee_code', code)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }

      // Transform data to match component expectations
      return {
        id: data.id,
        employeeCode: data.employee_code,
        name: data.name,
        email: data.email,
        phone: data.phone,
        department: data.department,
        position: data.position,
        hireDate: data.hire_date,
        isActive: data.is_active,
        userProfile: data.user_profile
      };
    } catch (err) {
      console.error('Error looking up employee:', err);
      throw err;
    }
  };

  // Handle keypad input
  const handleKeyPress = useCallback((key) => {
    if (isProcessing) return;
    
    setError(null);
    
    if (key === '*' || key === '#') {
      // Special keys - could be used for additional functionality
      return;
    }
    
    if (employeeCode?.length < 6) {
      const newCode = employeeCode + key;
      setEmployeeCode(newCode);
      
      // Auto-lookup employee when code is complete (6 digits)
      if (newCode.length === 6) {
        lookupEmployee(newCode).then(employee => {
          if (employee) {
            setCurrentEmployee(employee);
          } else {
            setCurrentEmployee(null);
          }
        }).catch(err => {
          console.error('Error looking up employee:', err);
          setCurrentEmployee(null);
        });
      }
    }
  }, [employeeCode?.length, isProcessing]);

  // Clear employee code
  const handleClear = useCallback(() => {
    if (isProcessing) return;
    setEmployeeCode('');
    setCurrentEmployee(null);
    setError(null);
  }, [isProcessing]);

  // Double punch prevention disabled: always allow immediate punches
  const checkDoublePunch = async () => {
    return true;
  };

  // Add punch to recent punches tracking
  const addRecentPunch = (employeeId, action) => {
    const punchKey = `${employeeId}-${action}-${Date.now()}`;
    setRecentPunches(prev => {
      const newSet = new Set(prev);
      newSet?.add({ key: punchKey, timestamp: Date.now() });
      
      // Clean up old punches (older than 60 seconds)
      const now = Date.now();
      for (const punch of newSet) {
        if (now - punch?.timestamp > 60000) {
          newSet?.delete(punch);
        }
      }
      
      return newSet;
    });
  };

  // Handle clock in/out actions
  const handleClockAction = async (action) => {
    if (!employeeCode || employeeCode?.length < 4 || isProcessing) {
      setError('Bitte einen gültigen Mitarbeitercode eingeben');
      return;
    }

    setIsProcessing(true);
    setLastAction(action);
    setError(null);

    try {
      // Look up employee in database
      const employee = await lookupEmployee(employeeCode);
      if (!employee) {
        setError('Mitarbeitercode nicht gefunden. Bitte erneut versuchen.');
        setIsProcessing(false);
        setLastAction(null);
        return;
      }

      // Check if employee has already clocked in today
      if (action === 'clock-in') {
        const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD format
        const { data: todayEntries, error: todayError } = await supabase
          .from('time_entries')
          .select('id, clock_in_time, clock_out_time')
          .eq('employee_id', employee.id)
          .gte('clock_in_time', `${today} 00:00:00`)
          .lt('clock_in_time', `${today} 23:59:59`)
          .order('clock_in_time', { ascending: false });

        if (todayError) {
          throw todayError;
        }

        // Check if there's already ANY clock-in entry for today (regardless of clock-out status)
        if (todayEntries && todayEntries.length > 0) {
          console.log('Found existing clock-in entries:', todayEntries);
          setIsProcessing(false);
          setLastAction(null);
          setShowAlreadyClockedInModal(true);
          console.log('Setting showAlreadyClockedInModal to true');
          return;
        }
      }

      // Create time entry in database
      const now = new Date();
      const nowLocal = action === 'clock-in' ? getAdjustedClockInTime() : getAdjustedClockOutTime();
      const timeEntryData = {
        employee_id: employee.id,
        clock_in_time: nowLocal,
        entry_type: 'regular'
        // Note: clock_in_day_utc and clock_in_day_berlin are generated columns
        // and will be automatically calculated by the database
      };

      // For clock out, we need to find the most recent clock in entry
      if (action === 'clock-out') {
        const { data: lastEntry, error: lastEntryError } = await supabase
          .from('time_entries')
          .select('id, clock_in_time')
          .eq('employee_id', employee.id)
          .is('clock_out_time', null)
          .order('clock_in_time', { ascending: false })
          .limit(1)
          .single();

        if (lastEntryError || !lastEntry) {
          setError('Kein aktiver Einstempelvorgang gefunden. Bitte zuerst einstempeln.');
          setIsProcessing(false);
          setLastAction(null);
          return;
        }

        // Calculate time difference for logging
        const clockInTime = new Date(lastEntry.clock_in_time);
        const clockOutTime = new Date(nowLocal);
        const timeDifferenceMs = clockOutTime - clockInTime;
        const timeDifferenceHours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
        const timeDifferenceMinutes = Math.floor((timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60));
        const timeDifferenceSeconds = Math.floor((timeDifferenceMs % (1000 * 60)) / 1000);

        console.log('=== CLOCK OUT LOGGING ===');
        console.log(`Employee: ${employee.name} (${employee.employeeCode})`);
        console.log(`Clock In Time: ${clockInTime.toLocaleString('de-DE')}`);
        console.log(`Clock Out Time: ${clockOutTime.toLocaleString('de-DE')}`);
        console.log(`Time Difference: ${timeDifferenceHours}h ${timeDifferenceMinutes}m ${timeDifferenceSeconds}s`);
        console.log(`Total Minutes: ${Math.floor(timeDifferenceMs / (1000 * 60))}`);
        console.log('========================');

        // Update the existing entry with clock out time (rounded)
        const { error: updateError } = await supabase
          .from('time_entries')
          .update({
            clock_out_time: nowLocal, // This is now the rounded clock-out time
            total_hours: null // Will be calculated by trigger
          })
          .eq('id', lastEntry.id);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Clock in - create new entry
        console.log('=== CLOCK IN LOGGING ===');
        console.log(`Employee: ${employee.name} (${employee.employeeCode})`);
        console.log(`Clock In Time: ${new Date(nowLocal).toLocaleString('de-DE')}`);
        console.log(`Raw Time String: ${nowLocal}`);
        console.log('========================');

        const { error: insertError } = await supabase
          .from('time_entries')
          .insert([timeEntryData]);

        if (insertError) {
          throw insertError;
        }
      }

      // Add to recent punches for UI tracking
      addRecentPunch(employee.id, action);

      // Calculate today's working hours
      let workingHours = '0h 0m';
      try {
        const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD format
        const { data: todayEntries, error: todayError } = await supabase
          .from('time_entries')
          .select('clock_in_time, clock_out_time, total_hours')
          .eq('employee_id', employee.id)
          .gte('clock_in_time', `${today} 00:00:00`)
          .lt('clock_in_time', `${today} 23:59:59`)
          .order('clock_in_time', { ascending: true });

        if (!todayError && todayEntries && todayEntries.length > 0) {
          let totalMinutes = 0;
          
          for (const entry of todayEntries) {
            if (entry.clock_in_time && entry.clock_out_time) {
              const clockIn = new Date(entry.clock_in_time);
              const clockOut = new Date(entry.clock_out_time);
              const diffMs = clockOut - clockIn;
              if (diffMs > 0) {
                totalMinutes += diffMs / (1000 * 60); // Convert to minutes
              }
            }
          }
          
          // Format as "Xh Ym"
          const hours = Math.floor(totalMinutes / 60);
          const minutes = Math.round(totalMinutes % 60);
          workingHours = `${hours}h ${minutes}m`;
        }
      } catch (hoursError) {
        console.error('Error calculating working hours:', hoursError);
        // Keep default '0h 0m' on error
      }

      // Navigate to success page with employee data
      navigate('/clock-success-confirmation', {
        state: {
          employee: {
            id: employee.id,
            name: employee.name,
            employeeCode: employee.employeeCode,
            department: employee.department,
            position: employee.position,
            photo: null // Could be added later if needed
          },
          action,
          timestamp: nowLocal, // This will show the adjusted time (8:00 AM if before 8:00 AM)
          workingHours,
          source: 'kiosk'
        }
      });

    } catch (error) {
      console.error('Clock action failed:', error);
      setError('Aktion fehlgeschlagen. Bitte erneut versuchen.');
      setIsProcessing(false);
      setLastAction(null);
    }
  };

  // Handle QR code scan result
  const handleQRScanResult = (scannedCode) => {
    setShowQRScanner(false);
    setEmployeeCode(scannedCode);
    setError(null);
  };

  // Handle idle timeout
  const handleIdleTimeout = () => {
    setEmployeeCode('');
    setCurrentEmployee(null);
    setError(null);
    setIsProcessing(false);
    setLastAction(null);
  };

  // Reset kiosk state
  const handleReset = () => {
    setEmployeeCode('');
    setCurrentEmployee(null);
    setError(null);
    setIsProcessing(false);
    setLastAction(null);
    setShowQRScanner(false);
  };

  return (
    <div
      className="bg-background flex flex-col overflow-hidden"
      style={{ height: '100dvh', minHeight: 'calc(var(--app-vh, 1vh) * 100)' }}
    >
      <IdleTimeoutHandler
        timeout={30000}
        warningTime={10000}
        onTimeout={handleIdleTimeout}
        onReset={handleReset}
        isActive={!isProcessing}
      >
        {/* Header */}
        <KioskHeader 
          currentTime={currentTime}
          showAdminAccess={true}
          compact={isCompact}
        />

        {/* Main Content */}
        <main className={`flex-1 flex items-center justify-center ${isCompact ? 'p-4' : 'p-6'} overflow-auto`}>
          <div className="w-full max-w-4xl mx-auto">
            {/* <div className={`text-center ${isCompact ? 'mb-4' : 'mb-8'}`}>
              <h1 className={`${isCompact ? 'text-2xl' : 'text-4xl'} font-semibold text-text-primary mb-2`}>
                Mitarbeiter Ein-/Aus-stempeln
              </h1>
              <p className={`${isCompact ? 'text-base' : 'text-xl'} text-text-secondary`}>
                Mitarbeitercode eingeben oder Ausweis scannen
              </p>
            </div> */}

            {/* Error Display */}
            {error && (
              <div className={`max-w-md mx-auto ${isCompact ? 'mb-4' : 'mb-6'}`}>
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={20} className="text-error" />
                    <span className="text-error font-medium">{error}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Numeric Keypad and Clock Action Buttons */}
            <div className={`flex flex-col lg:flex-row justify-center items-center lg:items-start gap-8 ${isCompact ? 'mb-4' : 'mb-8'}`}>
              {/* Keypad Section with Code Input */}
              <div className="flex-shrink-0 flex flex-col items-center">
                {/* Employee Code Input and Employee Info */}
                <div className={`relative ${isCompact ? 'mb-4' : 'mb-6'}`}>
                  {/* Employee Code Input - centered */}
                  <div className="max-w-sm mx-auto">
                    <EmployeeCodeInput
                      value={employeeCode}
                      maxLength={6}
                      placeholder="Mitarbeitercode eingeben"
                      compact={isCompact}
                    />
                  </div>
                  
                  {/* Current Employee Display - positioned absolutely to the right */}
                  {currentEmployee && (
                    <div className="absolute top-0 left-full ml-6 hidden lg:block max-w-md">
                      <div className={`bg-card border border-border rounded-lg ${isCompact ? 'p-4' : 'p-6'} text-center shadow-lg`}>
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="User" size={32} className="text-primary" />
                        </div>
                        <h3 className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-text-primary mb-2`}>
                          {currentEmployee.name}
                        </h3>
                        <p className="text-base text-text-secondary mb-2">
                          {currentEmployee.department}
                        </p>
                        <p className="text-sm text-text-secondary">
                          {currentEmployee.position}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Current Employee Display - below input on mobile */}
                  {currentEmployee && (
                    <div className="lg:hidden mt-4 max-w-md mx-auto">
                      <div className={`bg-card border border-border rounded-lg ${isCompact ? 'p-4' : 'p-6'} text-center shadow-lg`}>
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="User" size={32} className="text-primary" />
                        </div>
                        <h3 className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-text-primary mb-2`}>
                          {currentEmployee.name}
                        </h3>
                        <p className="text-base text-text-secondary mb-2">
                          {currentEmployee.department}
                        </p>
                        <p className="text-sm text-text-secondary">
                          {currentEmployee.position}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Numeric Keypad */}
                <NumericKeypad
                  onKeyPress={handleKeyPress}
                  onClear={handleClear}
                  disabled={isProcessing}
                  compact={isCompact}
                />
              </div>

              {/* Clock Action Buttons */}
              <div className="flex-shrink-0 lg:mt-80">
                <ClockActionButtons
                  onClockIn={() => handleClockAction('clock-in')}
                  onClockOut={() => handleClockAction('clock-out')}
                  disabled={employeeCode?.length < 4}
                  isProcessing={isProcessing}
                  lastAction={lastAction}
                  employeeCode={employeeCode}
                  compact={isCompact}
                />
              </div>
            </div>

            {/* Alternative Actions */}
            {/* <div className={`flex justify-center space-x-4 ${isCompact ? 'mt-2' : 'mt-4'}`}>
              <Button
                variant="outline"
                size={isCompact ? 'md' : 'lg'}
                onClick={() => setShowQRScanner(true)}
                disabled={isProcessing}
                iconName="QrCode"
                iconPosition="left"
                className="touch-feedback"
              >
                Ausweis scannen
              </Button>
              
              <Button
                variant="ghost"
                size={isCompact ? 'md' : 'lg'}
                onClick={handleReset}
                disabled={isProcessing}
                iconName="RotateCcw"
                iconPosition="left"
                className="touch-feedback"
              >
                Zurücksetzen
              </Button>
            </div> */}
          </div>
        </main>

        {/* Footer */}
        <KioskFooter compact={isCompact} />

        {/* QR Code Scanner */}
        <QRCodeScanner
          isActive={showQRScanner}
          onScanResult={handleQRScanResult}
          onClose={() => setShowQRScanner(false)}
        />

        {/* Accessibility Controls */}
        {/* <AccessibilityControls /> */}
      </IdleTimeoutHandler>

      {/* Already Clocked In Modal */}
      {console.log('showAlreadyClockedInModal state:', showAlreadyClockedInModal)}
      {showAlreadyClockedInModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxWidth: '400px',
              width: '100%',
              padding: '24px'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div 
                style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <Icon name="AlertTriangle" size={32} style={{ color: '#f59e0b' }} />
              </div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                color: '#111827', 
                marginBottom: '8px' 
              }}>
                Bereits eingestempelt
              </h3>
              <p style={{ 
                color: '#6b7280', 
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                Sie haben sich heute bereits eingestempelt. Nur ein Einstempeln pro Tag erlaubt.
              </p>
              <button
                onClick={() => {
                  setShowAlreadyClockedInModal(false);
                  setEmployeeCode('');
                  setCurrentEmployee(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskClockInterface;